import { useState, useMemo } from 'react'

interface SortState {
  field: string
  direction: 'asc' | 'desc'
}

export function useTableSort<T extends Record<string, unknown>>(
  data: T[],
  defaultField: string,
  defaultDirection: 'asc' | 'desc' = 'asc'
) {
  const [sort, setSort] = useState<SortState>({ field: defaultField, direction: defaultDirection })

  const toggle = (field: string) => {
    setSort(prev =>
      prev.field === field
        ? { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { field, direction: 'asc' }
    )
  }

  const sorted = useMemo(() => {
    return [...data].sort((a, b) => {
      const aVal = a[sort.field]
      const bVal = b[sort.field]

      // Nulls last
      if (aVal == null && bVal == null) return 0
      if (aVal == null) return 1
      if (bVal == null) return -1

      // Numeric comparison
      const aNum = Number(aVal)
      const bNum = Number(bVal)
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return sort.direction === 'asc' ? aNum - bNum : bNum - aNum
      }

      // String comparison
      const cmp = String(aVal).localeCompare(String(bVal))
      return sort.direction === 'asc' ? cmp : -cmp
    })
  }, [data, sort.field, sort.direction])

  return { sorted, sort, toggle }
}
